import { useState, useCallback, useRef } from 'react';
import { getAccounts, getContacts, getIndustries, getDealStages } from '../services/radar';
import { getTranscripts } from '../services/transcriptService';

const DEFAULT_ACCOUNT_PARAMS = {
  search: '',
  industry: '',
  dealStage: '',
  page: 1,
  limit: 25
};

const DEFAULT_CONTACT_PARAMS = {
  search: '',
  source: '',
  company: '',
  page: 1,
  limit: 50
};

export const useRadarData = (user) => {
  // Accounts state
  const [accounts, setAccounts] = useState([]);
  const [accountsError, setAccountsError] = useState('');
  const [tableLoading, setTableLoading] = useState(false);
  const [accountRequest, setAccountRequest] = useState(DEFAULT_ACCOUNT_PARAMS);
  const [accountsPagination, setAccountsPagination] = useState(null);
  const accountRequestRef = useRef(DEFAULT_ACCOUNT_PARAMS);

  // Contacts state
  const [contacts, setContacts] = useState([]);
  const [contactsError, setContactsError] = useState('');
  const [contactsTableLoading, setContactsTableLoading] = useState(false);
  const [contactRequest, setContactRequest] = useState(DEFAULT_CONTACT_PARAMS);
  const [contactsPagination, setContactsPagination] = useState(null);
  const contactRequestRef = useRef(DEFAULT_CONTACT_PARAMS);

  // Transcripts state
  const [transcripts, setTranscripts] = useState([]);
  const [transcriptError, setTranscriptError] = useState('');

  // Industries and deal stages
  const [industries, setIndustries] = useState([]);
  const [dealStages, setDealStages] = useState([]);

  // Data loading functions
  const loadRadar = useCallback(async (overrides = {}) => {
    try {
      setTableLoading(true);
      setAccountsError('');

      const nextRequest = {
        ...DEFAULT_ACCOUNT_PARAMS,
        ...accountRequestRef.current,
        ...overrides
      };

      accountRequestRef.current = nextRequest;
      setAccountRequest(nextRequest);

      const { search, industry, dealStage, page, limit } = nextRequest;

      const params = {
        search,
        page,
        limit
      };

      if (industry) params.industry = industry;
      if (dealStage) params.dealStage = dealStage;

      const response = await getAccounts(params);
      console.log('📊 useRadarData - Raw API response:', response);
      
      // API returns: { status: 'success', data: { accounts: [...], pagination: {...} } }
      // getAccounts already returns response.data from axios, so response = { status: 'success', data: {...} }
      const accountsData = response?.data?.accounts || response?.accounts || [];
      const paginationData = response?.data?.pagination || response?.pagination || null;
      
      console.log('📊 useRadarData - Accounts loaded:', {
        accountsData,
        length: accountsData.length,
        sampleAccount: accountsData[0],
        response,
        responseStructure: {
          hasData: !!response?.data,
          hasAccounts: !!response?.data?.accounts,
          hasDirectAccounts: !!response?.accounts,
          responseKeys: Object.keys(response || {})
        }
      });
      setAccounts(accountsData);
      setAccountsPagination(paginationData);
    } catch (err) {
      console.error('Error loading accounts:', err);
      setAccountsError('Failed to load accounts');
    } finally {
      setTableLoading(false);
    }
  }, []);

  const loadContacts = useCallback(async (overrides = {}) => {
    try {
      setContactsTableLoading(true);
      setContactsError('');

      const nextRequest = {
        ...DEFAULT_CONTACT_PARAMS,
        ...contactRequestRef.current,
        ...overrides
      };

      contactRequestRef.current = nextRequest;
      setContactRequest(nextRequest);

      const { search, source, company, page, limit } = nextRequest;

      const params = {
        search,
        page,
        limit
      };

      if (source) params.source = source;
      if (company) params.company = company;
      
      const response = await getContacts(params);
      setContacts(response.contacts || []);
      setContactsPagination({
        totalItems: response.total ?? null,
        currentPage: response.page ?? page,
        itemsPerPage: response.limit ?? limit,
        totalPages: response.total && response.limit
          ? Math.ceil(response.total / response.limit)
          : null
      });
    } catch (err) {
      console.error('Error loading contacts:', err);
      setContactsError('Failed to load contacts');
    } finally {
      setContactsTableLoading(false);
    }
  }, []);

  const loadTranscripts = useCallback(async () => {
    try {
      if (!user) {
        console.log('🔒 User not authenticated, skipping transcript load');
        return;
      }
      
      console.log('🔍 Loading transcripts with status filter: saved');
      const response = await getTranscripts({ 
        limit: 1000,
        status: 'saved'
      });
      
      console.log('📊 Transcripts API response:', response);
      setTranscripts(response.transcripts || []);
    } catch (err) {
      console.error(' Error loading transcripts:', err);
      if (err.message.includes('401') || err.message.includes('Unauthorized')) {
        console.log('🔒 Authentication error - user may not be logged in');
        setTranscripts([]);
      }
    }
  }, [user]);

  const loadIndustries = useCallback(async () => {
    try {
      const response = await getIndustries();
      setIndustries(response.data || []);
    } catch (err) {
      console.error('Error loading industries:', err);
    }
  }, []);

  const loadDealStages = useCallback(async () => {
    try {
      const response = await getDealStages();
      setDealStages(response.data || []);
    } catch (err) {
      console.error('Error loading deal stages:', err);
    }
  }, []);

  return {
    // Accounts
    accounts,
    setAccounts,
    accountsError,
    setAccountsError,
    tableLoading,
    setTableLoading,
    loadRadar,
    loadAccounts: loadRadar, // Add alias for compatibility
    accountRequest,
    accountsPagination,

    // Contacts
    contacts,
    setContacts,
    contactsError,
    setContactsError,
    contactsTableLoading,
    setContactsTableLoading,
    loadContacts,
    contactRequest,
    contactsPagination,

    // Transcripts
    transcripts,
    setTranscripts,
    transcriptError,
    setTranscriptError,
    loadTranscripts,

    // Industries and deal stages
    industries,
    setIndustries,
    dealStages,
    setDealStages,
    loadIndustries,
    loadDealStages
  };
};
